import { reaction } from 'mobx'
import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useRunState } from '../../provider/useRunState'
import type { TrackRegionState } from './trackController'

type BorderVisibilityControllerProps = {
    borderGeo: THREE.Group
}

export function BorderVisibilityController({
    borderGeo,
}: BorderVisibilityControllerProps) {
    const runState = useRunState()
    const unlockedRegionsRef = useRef<Set<1 | 2 | 3 | 4>>(new Set([1]))

    useEffect(() => {
        const syncVisibility = () => {
            if (runState.phase === 'idle') {
                unlockedRegionsRef.current = new Set([1])
            }

            const regionState = runState.trackRegionState
            unlockedRegionsRef.current = getUnlockedBorderRegionsForState(
                unlockedRegionsRef.current,
                regionState,
            )

            if (!regionState.activeTransition) {
                setBorderRegionVisibility(
                    borderGeo,
                    unlockedRegionsRef.current,
                )
                return
            }

            const nextRegion = getNextBorderRegion(regionState.activeRegion)
            if (nextRegion && !unlockedRegionsRef.current.has(nextRegion)) {
                unlockedRegionsRef.current = new Set([
                    ...unlockedRegionsRef.current,
                    nextRegion,
                ])
            }

            setBorderRegionVisibility(
                borderGeo,
                getVisibleBorderRegionsForTransition(
                    unlockedRegionsRef.current,
                    regionState.activeRegion,
                ),
            )
        }

        syncVisibility()

        return reaction(
            () => [
                runState.phase,
                runState.trackRegionState.activeRegion,
                runState.trackRegionState.activeTransition,
            ] as const,
            syncVisibility,
        )
    }, [borderGeo, runState])

    return null
}

function getUnlockedBorderRegionsForState(
    unlockedRegions: ReadonlySet<1 | 2 | 3 | 4>,
    regionState: TrackRegionState,
): Set<1 | 2 | 3 | 4> {
    const nextUnlocked = new Set(unlockedRegions)

    for (const region of [1, 2, 3, 4] as const) {
        if (region <= regionState.activeRegion) {
            nextUnlocked.add(region)
        }
    }

    if (regionState.activeTransition) {
        const nextRegion = getNextBorderRegion(regionState.activeRegion)
        if (nextRegion) {
            nextUnlocked.add(nextRegion)
        }
    }

    return nextUnlocked
}

function setBorderRegionVisibility(
    borderGeo: THREE.Group,
    visibleRegions: ReadonlySet<1 | 2 | 3 | 4>,
) {
    for (const region of [1, 2, 3, 4] as const) {
        const mesh = borderGeo.getObjectByName(`BorderRegion${region}`)
        if (!mesh) {
            continue
        }
        mesh.visible = visibleRegions.has(region)
    }
}

function getVisibleBorderRegionsForTransition(
    unlockedRegions: ReadonlySet<1 | 2 | 3 | 4>,
    activeRegion: 1 | 2 | 3 | 4,
) {
    const visibleRegions = new Set<1 | 2 | 3 | 4>()

    for (const region of unlockedRegions) {
        if (region > activeRegion) {
            visibleRegions.add(region)
        }
    }

    return visibleRegions
}

function getNextBorderRegion(
    region: 1 | 2 | 3 | 4,
): 2 | 3 | 4 | null {
    switch (region) {
        case 1:
            return 2
        case 2:
            return 3
        case 3:
            return 4
        case 4:
            return null
    }
}
